import React, { useState, useEffect } from 'react';
import { Building2, Plus, Pencil, Trash2, MapPin, Phone, Mail, Check, X, CreditCard, ShieldCheck, ShieldAlert, Clock, ArrowRight, ArrowLeft, Star, Send, MessageCircle, Car, Upload, Video } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { WhatsAppTemplateSelect } from '@/components/ui/whatsapp-template-select';
import { toast } from 'sonner';
import axios from 'axios';




const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SWISS_CANTONS = [
  ['ZH', 'Zürich'], ['BE', 'Bern'], ['LU', 'Luzern'], ['UR', 'Uri'], ['SZ', 'Schwyz'],
  ['OW', 'Obwalden'], ['NW', 'Nidwalden'], ['GL', 'Glarus'], ['ZG', 'Zug'], ['FR', 'Freiburg'],
  ['SO', 'Solothurn'], ['BS', 'Basel-Stadt'], ['BL', 'Basel-Landschaft'], ['SH', 'Schaffhausen'],
  ['AR', 'Appenzell Ausserrhoden'], ['AI', 'Appenzell Innerrhoden'], ['SG', 'St. Gallen'],
  ['GR', 'Graubünden'], ['AG', 'Aargau'], ['TG', 'Thurgau'], ['TI', 'Tessin'], ['VD', 'Waadt'],
  ['VS', 'Wallis'], ['NE', 'Neuenburg'], ['GE', 'Genf'], ['JU', 'Jura'],
];

const emptyForm = {
  name: '',
  short_name: '',
  address: '',
  canton: '',
  phone: '',
  email: '',
  // Google-Bewertungslink für automatische Review-Anfragen per WhatsApp
  google_review_link: '',
  google_review_override: false,
  google_review_enabled: false,
  google_review_after_trainings: '',
  google_review_template: '',
  // Iter 299: Cross-Studio-Roaming — wenn true, sehen alle Kunden dieses Studio
  // im Booking-Picker. False = nur explizit zugewiesene Kunden.
  is_public_bookable: true,
  coming_soon: false,
  // WhatsApp Auto-Reschedule: Stunden bis zur Erinnerung (leer = Standard 3h, 0 = aus)
  reschedule_reminder_hours: '',
  // Probetraining-Standortinfo (Anfahrt/Parken vor dem Probetraining)
  location_notify_enabled: false,
  location_notify_hours_before: '',
  entrance_info: '',
  parking_info: '',
  parking_page_url: '',
  location_image_url: '',
  location_video_url: '',
  location_wa_foto_template: '',
  location_wa_video_template: '',
  // Studio-spezifische Payrexx-Konfiguration (Franchise)
  payrexx_enabled: false,
  payrexx_instance_name: '',
  payrexx_api_key: '',
  payrexx_webhook_secret: '',
  // Studio-spezifische Rechnungs-Overrides (Franchise — alle optional)
  invoice_override_enabled: false, // UI-only Toggle (lokal)
  company_name: '',
  company_street: '',
  company_postal_code: '',
  company_city: '',
  company_phone: '',
  company_email: '',
  company_website: '',
  tax_number: '',
  logo_url: '',
  bank_iban: '',
  bank_account_holder: '',
  bank_name: '',
  bank_bic: '',
  footer_text: '',
  pdf_closing_text: '',
  pdf_greeting_text: '',
  invoice_prefix: '',
  payment_terms_days: '',
};

const StudiosPage = ({ embedded = false }) => {
  const [studios, setStudios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingStudio, setEditingStudio] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [integrity, setIntegrity] = useState(null);
  const [reassignTo, setReassignTo] = useState({});
  const [reassigning, setReassigning] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [waTemplates, setWaTemplates] = useState([]);
  const [waTplStatus, setWaTplStatus] = useState(null);
  const [waTplBusy, setWaTplBusy] = useState(false);

  useEffect(() => {
    fetchStudios();
    fetchIntegrity();
    fetchWaTemplates();
  }, []);

  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    if (p.get('new') === '1') {
      resetForm();
      setDialogOpen(true);
      toast.success('Lizenz aktiv – lege jetzt dein neues Studio an.');
      window.history.replaceState({}, '', '/studios');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchWaTemplates = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/studios/wa-templates`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setWaTemplates(res.data?.templates || []);
    } catch (e) {
      setWaTemplates([]);
    }
  };

  const fetchIntegrity = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${BACKEND_URL}/api/admin/diagnostics/studio-integrity`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setIntegrity(res.data);
    } catch (e) {
      // Diagnose optional — bei Fehler einfach keine Warnung zeigen.
      setIntegrity(null);
    }
  };

  const handleReassign = async (fromStudioId) => {
    const toStudioId = reassignTo[fromStudioId];
    if (!toStudioId) {
      toast.error('Bitte ein Ziel-Studio wählen');
      return;
    }
    setReassigning(fromStudioId);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${BACKEND_URL}/api/admin/diagnostics/studio-reassign`,
        { from_studio_id: fromStudioId, to_studio_id: toStudioId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`${res.data.appointments_updated} Termine · ${res.data.customers_updated} Kunden → ${res.data.to_studio_name}`);
      await fetchIntegrity();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Zuordnung fehlgeschlagen');
    } finally {
      setReassigning(null);
    }
  };

  const fetchStudios = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BACKEND_URL}/api/studios/all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStudios(response.data || []);
    } catch (error) {
      console.error('Error fetching studios:', error);
      toast.error('Fehler beim Laden der Studios');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      toast.error('Studio-Name ist erforderlich');
      return;
    }

    // Validierung: Wenn Payrexx aktiviert → mindestens Instance & API-Key müssen
    // gesetzt sein (oder im Backend bereits hinterlegt sein).
    if (formData.payrexx_enabled) {
      const hasInstance = !!formData.payrexx_instance_name?.trim();
      const hasKey = !!formData.payrexx_api_key?.trim() || editingStudio?.payrexx_api_key_set;
      if (!hasInstance || !hasKey) {
        toast.error('Studio-Payrexx benötigt mindestens Instance-Name & API-Key.');
        return;
      }
    }

    try {
      const token = localStorage.getItem('token');

      // Backend-Konvention: leere Strings für Secrets = "nicht ändern".
      const payload = { ...formData };
      // UI-only toggle entfernen
      delete payload.invoice_override_enabled;
      // Erinnerungs-Stunden: leer → null (Standard), sonst Ganzzahl
      payload.reschedule_reminder_hours =
        formData.reschedule_reminder_hours === '' || formData.reschedule_reminder_hours === null
          ? null
          : parseInt(formData.reschedule_reminder_hours, 10);

      // Probetraining-Standortinfo: Stunden leer → null (Backend-Default 6)
      payload.location_notify_hours_before =
        formData.location_notify_hours_before === '' || formData.location_notify_hours_before === null
          ? null
          : parseInt(formData.location_notify_hours_before, 10);

      // Wenn Rechnungs-Override AUS → alle Override-Felder leeren (Backend
      // löscht / setzt auf leer → Resolver fällt automatisch auf Default zurück).
      if (!formData.invoice_override_enabled) {
        INVOICE_OVERRIDE_FIELDS.forEach((f) => { payload[f] = ''; });
      } else {
        // Zahlungsziel: leer → '' (Default greift), sonst Ganzzahl
        payload.payment_terms_days =
          formData.payment_terms_days === '' || formData.payment_terms_days === null
            ? '' : parseInt(formData.payment_terms_days, 10);
      }

      if (editingStudio) {
        await axios.put(
          `${BACKEND_URL}/api/studios/${editingStudio.id}`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Studio aktualisiert');
      } else {
        const res = await axios.post(
          `${BACKEND_URL}/api/studios`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Studio erstellt');
        const created = res.data || {};
        const newId = created.id;
        const newCanton = (formData.canton || '').toUpperCase();
        if (newId && newCanton) {
          const year = new Date().getFullYear();
          toast(`Feiertage für ${newCanton} ${year} importieren?`, {
            duration: 12000,
            action: {
              label: 'Importieren',
              onClick: async () => {
                try {
                  const imp = await axios.post(
                    `${BACKEND_URL}/api/admin/holidays/import-swiss`,
                    { year, canton: newCanton, studio_id: newId },
                    { headers: { Authorization: `Bearer ${token}` } }
                  );
                  toast.success(`${imp.data?.imported ?? 0} Feiertage importiert`);
                } catch (err) {
                  toast.error(err.response?.data?.detail || 'Import fehlgeschlagen');
                }
              },
            },
          });
        }
      }

      setDialogOpen(false);
      resetForm();
      fetchStudios();
    } catch (error) {
      console.error('Error saving studio:', error);
      if (error.response?.status === 402) {
        toast.error('Zusätzliche Studio-Lizenz nötig. Bitte im Abo-Bereich freischalten.', {
          duration: 10000,
          action: { label: 'Zum Abo-Bereich', onClick: () => { window.location.href = '/admin/subscription'; } },
        });
      } else {
        toast.error(error.response?.data?.detail || 'Fehler beim Speichern');
      }
    }
  };

  // Welche Felder zählen als „Rechnungs-Override gepflegt"?
  const INVOICE_OVERRIDE_FIELDS = [
    'company_name', 'company_street', 'company_postal_code', 'company_city',
    'company_phone', 'company_email', 'company_website', 'tax_number',
    'logo_url', 'bank_iban', 'bank_account_holder', 'bank_name', 'bank_bic',
    'footer_text', 'pdf_closing_text', 'pdf_greeting_text', 'invoice_prefix',
    'payment_terms_days',
  ];

  const handleEdit = (studio) => {
    setEditingStudio(studio);
    const anyInvoiceOverride = INVOICE_OVERRIDE_FIELDS.some(
      (f) => studio[f] && String(studio[f]).trim() !== ''
    );
    setFormData({
      name: studio.name || '',
      short_name: studio.short_name || '',
      address: studio.address || '',
      canton: studio.canton || '',
      phone: studio.phone || '',
      email: studio.email || '',
      google_review_link: studio.google_review_link || '',
      google_review_override: studio.google_review_override ?? false,
      google_review_enabled: studio.google_review_enabled ?? false,
      google_review_after_trainings: studio.google_review_after_trainings ?? '',
      google_review_template: studio.google_review_template || '',
      // Iter 299: Default = true wenn Feld fehlt (Bestandsstudios)
      is_public_bookable: studio.is_public_bookable !== false,
      coming_soon: !!studio.coming_soon,
      reschedule_reminder_hours: studio.reschedule_reminder_hours ?? '',
      location_notify_enabled: !!studio.location_notify_enabled,
      location_notify_hours_before: studio.location_notify_hours_before ?? '',
      entrance_info: studio.entrance_info || '',
      parking_info: studio.parking_info || '',
      parking_page_url: studio.parking_page_url || '',
      location_image_url: studio.location_image_url || '',
      location_video_url: studio.location_video_url || '',
      location_wa_foto_template: studio.location_wa_foto_template || '',
      location_wa_video_template: studio.location_wa_video_template || '',
      payrexx_enabled: !!studio.payrexx_enabled,
      payrexx_instance_name: studio.payrexx_instance_name || '',
      // Secrets werden NIE zurückgesendet — Input bleibt leer (= nicht ändern)
      payrexx_api_key: '',
      payrexx_webhook_secret: '',
      // Rechnungs-Overrides (Franchise)
      invoice_override_enabled: anyInvoiceOverride,
      company_name: studio.company_name || '',
      company_street: studio.company_street || '',
      company_postal_code: studio.company_postal_code || '',
      company_city: studio.company_city || '',
      company_phone: studio.company_phone || '',
      company_email: studio.company_email || '',
      company_website: studio.company_website || '',
      tax_number: studio.tax_number || '',
      logo_url: studio.logo_url || '',
      bank_iban: studio.bank_iban || '',
      bank_account_holder: studio.bank_account_holder || '',
      bank_name: studio.bank_name || '',
      bank_bic: studio.bank_bic || '',
      footer_text: studio.footer_text || '',
      pdf_closing_text: studio.pdf_closing_text || '',
      pdf_greeting_text: studio.pdf_greeting_text || '',
      invoice_prefix: studio.invoice_prefix || '',
      payment_terms_days: studio.payment_terms_days ?? '',
    });
    setDialogOpen(true);
  };

  const handleDelete = async (studio) => {
    if (studio.is_default) {
      toast.error('Standard-Studio kann nicht gelöscht werden');
      return;
    }

    if (!window.confirm(`Studio "${studio.name}" wirklich deaktivieren?`)) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${BACKEND_URL}/api/studios/${studio.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Studio deaktiviert');
      fetchStudios();
    } catch (error) {
      console.error('Error deleting studio:', error);
      toast.error(error.response?.data?.detail || 'Fehler beim Löschen');
    }
  };

  const handleSetDefault = async (studio) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${BACKEND_URL}/api/studios/${studio.id}`,
        { is_default: true },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`"${studio.name}" ist jetzt das Standard-Studio`);
      fetchStudios();
    } catch (error) {
      console.error('Error setting default:', error);
      toast.error('Fehler beim Setzen als Standard');
    }
  };

  const resetForm = () => {
    setEditingStudio(null);
    setFormData(emptyForm);
  };

  const handleLocationImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!editingStudio?.id) {
      toast.error('Bitte das Studio zuerst speichern, dann das Foto hochladen.');
      return;
    }
    setUploadingImage(true);
    try {
      const token = localStorage.getItem('token');
      const fd = new FormData();
      fd.append('image', file);
      const res = await axios.post(
        `${BACKEND_URL}/api/studios/${editingStudio.id}/location-image`,
        fd,
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } }
      );
      setFormData((p) => ({ ...p, location_image_url: res.data.location_image_url }));
      toast.success('Standortfoto hochgeladen');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload fehlgeschlagen');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleLocationVideoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!editingStudio?.id) {
      toast.error('Bitte das Studio zuerst speichern, dann das Video hochladen.');
      return;
    }
    if (file.size > 16 * 1024 * 1024) {
      toast.error('Video zu gross (max. 16 MB für WhatsApp)');
      return;
    }
    setUploadingVideo(true);
    try {
      const token = localStorage.getItem('token');
      const fd = new FormData();
      fd.append('video', file);
      const res = await axios.post(
        `${BACKEND_URL}/api/studios/${editingStudio.id}/location-video`,
        fd,
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } }
      );
      setFormData((p) => ({ ...p, location_video_url: res.data.location_video_url }));
      toast.success('Standortvideo hochgeladen');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload fehlgeschlagen');
    } finally {
      setUploadingVideo(false);
    }
  };

  const handleSubmitLocationTemplate = async () => {
    setWaTplBusy(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${BACKEND_URL}/api/studios/location-template/submit`,
        { button_url: formData.parking_page_url || 'https://www.strongfit.ch' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const fotoOk = res.data?.foto?.success;
      const videoOk = res.data?.video?.success;
      setWaTplStatus({ foto: fotoOk ? 'PENDING' : (res.data?.foto?.error || 'Fehler'), video: videoOk ? 'PENDING' : (res.data?.video?.error || 'Fehler') });
      if (fotoOk || videoOk) {
        toast.success('WhatsApp-Vorlage(n) bei Meta eingereicht (Freigabe läuft).');
      } else {
        toast.error('Keine Vorlage eingereicht – zuerst Foto/Video hochladen & speichern.');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Einreichung fehlgeschlagen');
    } finally {
      setWaTplBusy(false);
    }
  };

  const handleCheckLocationTemplate = async () => {
    setWaTplBusy(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(
        `${BACKEND_URL}/api/studios/location-template/status`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setWaTplStatus({ foto: res.data?.foto || 'NOT_FOUND', video: res.data?.video || 'NOT_FOUND' });
      toast.success(`Foto: ${res.data?.foto || 'NOT_FOUND'} · Video: ${res.data?.video || 'NOT_FOUND'}`);
    } catch (err) {
      toast.error('Status konnte nicht geladen werden');
    } finally {
      setWaTplBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-500"></div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8 max-w-6xl" data-testid="studios-page">
      {!embedded && (
        <a href="/settings" data-testid="studios-back-btn" className="inline-flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900 mb-4">
          <ArrowLeft className="h-4 w-4" /> Zurück zu Einstellungen
        </a>
      )}
      <div className="flex flex-col md:flex-row md:items-center justify-between mb-6 gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Building2 className="h-6 w-6 text-teal-600" />
            Studios verwalten
          </h1>
          <p className="text-gray-600 mt-1">
            Verwalten Sie Ihre Standorte und deren Einstellungen
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() => window.location.href = '/studios/migrate'}
            data-testid="studio-migrate-nav"
          >
            <ArrowRight className="h-4 w-4 mr-2" />
            Bulk-Migration
          </Button>
          <Button
            variant="outline"
            onClick={() => window.location.href = '/studios/hours'}
            data-testid="studio-hours-nav"
          >
            <Clock className="h-4 w-4 mr-2" />
            Öffnungszeiten &amp; Feiertage
          </Button>
          <Button
            onClick={() => { resetForm(); setDialogOpen(true); }}
            className="bg-teal-600 hover:bg-teal-700"
            data-testid="studio-create-btn"
          >
            <Plus className="h-4 w-4 mr-2" />
            Neues Studio
          </Button>
        </div>
      </div>

      {integrity && !integrity.ok && integrity.issues?.length > 0 && (
        <Card className="mb-6 border-amber-300 bg-amber-50" data-testid="studio-integrity-warning">
          <CardHeader className="pb-2">
            <CardTitle className="text-amber-800 text-base flex items-center gap-2">
              <ShieldAlert className="h-5 w-5 text-amber-600" />
              Verwaiste Studio-Zuordnung erkannt
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm text-amber-800">
              {integrity.affected_appointments} Termine und {integrity.affected_customers} Kunden verweisen auf ein Studio,
              das es nicht (mehr) gibt. Solche Termine werden im Kalender-Studio-Filter ausgeblendet.
              Ordne sie einem bestehenden Studio zu:
            </p>
            {integrity.issues.map((iss) => (
              <div
                key={iss.studio_id}
                className="flex flex-col sm:flex-row sm:items-center gap-2 bg-white border border-amber-200 rounded-lg p-3"
                data-testid={`studio-orphan-${iss.studio_id}`}
              >
                <div className="flex-1 text-sm text-gray-700 min-w-0">
                  <span className="font-mono text-xs text-gray-500 break-all">{iss.studio_id}</span>
                  <div className="text-gray-600">{iss.appointments} Termine · {iss.customers} Kunden</div>
                </div>
                <select
                  data-testid={`studio-reassign-select-${iss.studio_id}`}
                  className="border border-gray-300 rounded-md px-2 py-1.5 text-sm"
                  value={reassignTo[iss.studio_id] || ''}
                  onChange={(e) => setReassignTo({ ...reassignTo, [iss.studio_id]: e.target.value })}
                >
                  <option value="">Studio wählen…</option>
                  {studios.map((s) => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
                <Button
                  size="sm"
                  className="bg-amber-600 hover:bg-amber-700"
                  disabled={reassigning === iss.studio_id}
                  onClick={() => handleReassign(iss.studio_id)}
                  data-testid={`studio-reassign-btn-${iss.studio_id}`}
                >
                  {reassigning === iss.studio_id ? '…' : 'Zuordnen'}
                </Button>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {studios.map((studio) => (
          <Card
            key={studio.id}
            data-testid={`studio-card-${studio.id}`}
            className={`${!studio.is_active ? 'opacity-60' : ''} ${studio.is_default ? 'ring-2 ring-teal-500' : ''}`}
          >
            <CardHeader className="pb-2">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-2">
                  <Building2 className="h-5 w-5 text-teal-600" />
                  <div>
                    <CardTitle className="text-lg">{studio.name}</CardTitle>
                    {studio.short_name && (
                      <span className="text-xs text-gray-500">({studio.short_name})</span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  {studio.is_default && (
                    <span className="text-xs bg-teal-100 text-teal-700 px-2 py-0.5 rounded-full">
                      Standard
                    </span>
                  )}
                  {!studio.is_active && (
                    <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full">
                      Inaktiv
                    </span>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 text-sm text-gray-600 mb-4">
                {studio.address && (
                  <div className="flex items-center gap-2">
                    <MapPin className="h-4 w-4 flex-shrink-0" />
                    <span>{studio.address}</span>
                  </div>
                )}
                {studio.phone && (
                  <div className="flex items-center gap-2">
                    <Phone className="h-4 w-4 flex-shrink-0" />
                    <span>{studio.phone}</span>
                  </div>
                )}
                {studio.email && (
                  <div className="flex items-center gap-2">
                    <Mail className="h-4 w-4 flex-shrink-0" />
                    <span>{studio.email}</span>
                  </div>
                )}
                {studio.google_review_link && (
                  <div className="flex items-center gap-2 pt-1">
                    <Star className="h-4 w-4 flex-shrink-0 text-amber-500" />
                    <span
                      className="text-xs bg-amber-50 text-amber-700 px-2 py-0.5 rounded-full"
                      data-testid={`studio-review-link-set-${studio.id}`}
                    >
                      Bewertungslink hinterlegt
                    </span>
                  </div>
                )}
                {/* Payrexx-Status Pill */}
                <div className="flex items-center gap-2 pt-1">
                  <CreditCard className="h-4 w-4 flex-shrink-0" />
                  {studio.payrexx_enabled && studio.payrexx_api_key_set ? (
                    <span
                      className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full inline-flex items-center gap-1"
                      data-testid={`studio-payrexx-active-${studio.id}`}
                    >
                      <ShieldCheck className="h-3 w-3" />
                      Eigenes Payrexx ({studio.payrexx_instance_name || '—'})
                    </span>
                  ) : (
                    <span
                      className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full inline-flex items-center gap-1"
                      data-testid={`studio-payrexx-default-${studio.id}`}
                    >
                      <ShieldAlert className="h-3 w-3" />
                      Hauptkonto
                    </span>
                  )}
                </div>
              </div>

              <div className="flex items-center gap-2 pt-2 border-t">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleEdit(studio)}
                  data-testid={`studio-edit-btn-${studio.id}`}
                >
                  <Pencil className="h-3 w-3 mr-1" />
                  Bearbeiten
                </Button>
                {!studio.is_default && studio.is_active && (
                  <>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleSetDefault(studio)}
                      data-testid={`studio-set-default-btn-${studio.id}`}
                    >
                      <Check className="h-3 w-3 mr-1" />
                      Standard
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-red-600 hover:text-red-700"
                      onClick={() => handleDelete(studio)}
                      data-testid={`studio-delete-btn-${studio.id}`}
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {studios.length === 0 && (
        <div className="text-center py-12 text-gray-500">
          <Building2 className="h-12 w-12 mx-auto mb-4 opacity-50" />
          <p>Noch keine Studios vorhanden</p>
          <Button onClick={() => setDialogOpen(true)} className="mt-4">
            <Plus className="h-4 w-4 mr-2" />
            Erstes Studio erstellen
          </Button>
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingStudio ? 'Studio bearbeiten' : 'Neues Studio'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="name">Name *</Label>
                <Input
                  id="name"
                  data-testid="studio-form-name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="z.B. StrongFit Wetzikon"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="short_name">Kurzname</Label>
                <Input
                  id="short_name"
                  data-testid="studio-form-shortname"
                  value={formData.short_name}
                  onChange={(e) => setFormData({ ...formData, short_name: e.target.value })}
                  placeholder="z.B. Wetzikon"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="address">Adresse</Label>
                <Input
                  id="address"
                  data-testid="studio-form-address"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  placeholder="Strasse, PLZ Ort"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="canton">Kanton (für Feiertagsvorlage)</Label>
                <select
                  id="canton"
                  data-testid="studio-form-canton"
                  value={formData.canton}
                  onChange={(e) => setFormData({ ...formData, canton: e.target.value })}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <option value="">— Kein Kanton —</option>
                  {SWISS_CANTONS.map(([code, name]) => (
                    <option key={code} value={code}>{name} ({code})</option>
                  ))}
                </select>
                <p className="text-xs text-gray-400">Nach dem Anlegen bieten wir die passenden Feiertage dieses Kantons zum Import an.</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="phone">Telefon</Label>
                  <Input
                    id="phone"
                    data-testid="studio-form-phone"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="+41 44 123 45 67"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">E-Mail</Label>
                  <Input
                    id="email"
                    data-testid="studio-form-email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="studio@example.ch"
                  />
                </div>
              </div>

              {/* Google-Bewertungslink (pro Studio) */}
              <div className="space-y-2">
                <Label htmlFor="google_review_link" className="flex items-center gap-2">
                  <Star className="h-4 w-4 text-amber-500" />
                  Google-Bewertungslink
                </Label>
                <Input
                  id="google_review_link"
                  data-testid="studio-form-google-review-link"
                  value={formData.google_review_link}
                  onChange={(e) => setFormData({ ...formData, google_review_link: e.target.value })}
                  placeholder="https://g.page/r/..."
                />
                <p className="text-xs text-gray-500">
                  Wird für die automatische Bewertungs-Anfrage per WhatsApp verwendet
                  (siehe Karte „Google-Bewertungs-Automatik").
                </p>
              </div>

              {/* Google-Bewertungs-Automatik PRO STUDIO (Opt-in; sonst globale Einstellung) */}
              <div className="space-y-3 rounded-lg border border-amber-200 bg-amber-50/40 p-4">
                <div className="flex items-center justify-between">
                  <Label className="flex items-center gap-2 font-medium">
                    <Star className="h-4 w-4 text-amber-500" />
                    Eigene Bewertungs-Automatik für dieses Studio
                  </Label>
                  <Switch
                    checked={!!formData.google_review_override}
                    onCheckedChange={(c) => setFormData({ ...formData, google_review_override: !!c })}
                    data-testid="studio-form-review-override"
                  />
                </div>
                <p className="text-xs text-gray-500">
                  Aus = globale Einstellung (Flagship) gilt. Ein = An/Aus, Schwelle & Text unten gelten nur für dieses Studio.
                </p>
                {formData.google_review_override && (
                  <div className="space-y-3 pt-1">
                    <div className="flex items-center justify-between">
                      <Label>Automatik aktiv</Label>
                      <Switch
                        checked={!!formData.google_review_enabled}
                        onCheckedChange={(c) => setFormData({ ...formData, google_review_enabled: !!c })}
                        data-testid="studio-form-review-enabled"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label htmlFor="google_review_after_trainings">Anfrage nach ... Trainings</Label>
                      <Input
                        id="google_review_after_trainings"
                        type="number"
                        min="1"
                        className="w-32"
                        placeholder="z.B. 3"
                        value={formData.google_review_after_trainings}
                        onChange={(e) => setFormData({ ...formData, google_review_after_trainings: e.target.value })}
                        data-testid="studio-form-review-threshold"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label htmlFor="google_review_template">Nachrichten-Vorlage (leer = Standard)</Label>
                      <Textarea
                        id="google_review_template"
                        rows={5}
                        placeholder="Platzhalter: {first_name}, {training_count}, {studio_name}, {review_link}"
                        value={formData.google_review_template}
                        onChange={(e) => setFormData({ ...formData, google_review_template: e.target.value })}
                        data-testid="studio-form-review-template"
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* WhatsApp Auto-Reschedule: Erinnerungs-Zeitpunkt (pro Studio) */}
              <div className="space-y-2">
                <Label htmlFor="reschedule_reminder_hours" className="flex items-center gap-2">
                  <MessageCircle className="h-4 w-4 text-emerald-600" />
                  Verschiebe-Erinnerung nach (Stunden)
                </Label>
                <Input
                  id="reschedule_reminder_hours"
                  type="number"
                  min="0"
                  max="72"
                  data-testid="studio-form-reschedule-reminder-hours"
                  value={formData.reschedule_reminder_hours}
                  onChange={(e) => setFormData({ ...formData, reschedule_reminder_hours: e.target.value })}
                  placeholder="Standard: 3"
                />
                <p className="text-xs text-gray-500">
                  Nach wie vielen Stunden ohne Antwort eine freundliche WhatsApp-Erinnerung
                  zum „Termin verschieben"-Angebot gesendet wird. Leer = 3 Std, 0 = deaktiviert.
                </p>
              </div>

              {/* ============ Iter 299: Cross-Studio-Roaming (Public Bookable) ============ */}
              <div className="rounded-md border border-yellow-200 bg-yellow-50/40 p-3">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="flex items-center gap-2 text-sm font-medium text-gray-900">
                      <Building2 className="h-4 w-4 text-yellow-600" />
                      Öffentlich buchbar
                    </div>
                    <p className="text-xs text-gray-600 mt-1 leading-relaxed">
                      Wenn aktiviert, sehen ALLE Kunden dieses Studio im
                      Buchungs-Picker — auch ohne explizite Zuweisung.
                      Empfohlen für offene Standorte. Deaktivieren für
                      private/interne Studios.
                    </p>
                  </div>
                  <Switch
                    checked={formData.is_public_bookable}
                    onCheckedChange={(checked) => setFormData({ ...formData, is_public_bookable: !!checked })}
                    data-testid="studio-form-public-bookable"
                  />
                </div>
              </div>

              {/* ============ Coming Soon (Buchung im iFrame gesperrt) ============ */}
              <div className="rounded-md border border-orange-200 bg-orange-50/40 p-3">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="flex items-center gap-2 text-sm font-medium text-gray-900">
                      <Building2 className="h-4 w-4 text-orange-600" />
                      Coming Soon (Buchung gesperrt)
                    </div>
                    <p className="text-xs text-gray-600 mt-1 leading-relaxed">
                      Wenn aktiviert, wird das Studio im öffentlichen
                      Probetraining-iFrame zwar angezeigt, ist aber mit
                      "Coming Soon"-Badge gesperrt und nicht anklickbar.
                    </p>
                  </div>
                  <Switch
                    checked={formData.coming_soon}
                    onCheckedChange={(checked) => setFormData({ ...formData, coming_soon: !!checked })}
                    data-testid="studio-form-coming-soon"
                  />
                </div>
              </div>

              {/* ============ Probetraining-Standortinfo (Anfahrt/Parken) ============ */}
              <div className="rounded-md border border-sky-200 bg-sky-50/40 p-3 space-y-3" data-testid="studio-location-section">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="flex items-center gap-2 text-sm font-medium text-gray-900">
                      <MapPin className="h-4 w-4 text-sky-600" />
                      Probetraining-Standortinfo
                    </div>
                    <p className="text-xs text-gray-600 mt-1 leading-relaxed">
                      Schickt einige Stunden vor einem Probetraining automatisch Anfahrt,
                      Eingang, Parkplatz, Foto &amp; Video per E-Mail und WhatsApp.
                    </p>
                  </div>
                  <Switch
                    checked={formData.location_notify_enabled}
                    onCheckedChange={(checked) => setFormData({ ...formData, location_notify_enabled: !!checked })}
                    data-testid="studio-form-location-notify"
                  />
                </div>

                {formData.location_notify_enabled && (
                  <div className="space-y-3 pt-1">
                    <div className="space-y-1">
                      <Label htmlFor="location_notify_hours_before" className="flex items-center gap-2">
                        <Clock className="h-4 w-4 text-sky-600" /> Versand wie viele Stunden vorher?
                      </Label>
                      <Input
                        id="location_notify_hours_before"
                        type="number"
                        min="1"
                        max="72"
                        data-testid="studio-form-location-hours"
                        value={formData.location_notify_hours_before}
                        onChange={(e) => setFormData({ ...formData, location_notify_hours_before: e.target.value })}
                        placeholder="Standard: 6"
                      />
                    </div>

                    <div className="space-y-1">
                      <Label htmlFor="parking_info" className="flex items-center gap-2">
                        <Car className="h-4 w-4 text-sky-600" /> Parken (Text)
                      </Label>
                      <Textarea
                        id="parking_info"
                        rows={2}
                        data-testid="studio-form-parking-info"
                        value={formData.parking_info}
                        onChange={(e) => setFormData({ ...formData, parking_info: e.target.value })}
                        placeholder="Für dich stehen unsere StrongFit-Parkplätze Nr. 26 & 27 zur Verfügung."
                      />
                    </div>

                    <div className="space-y-1">
                      <Label htmlFor="entrance_info" className="flex items-center gap-2">
                        <MapPin className="h-4 w-4 text-sky-600" /> Eingang / Weg (Text)
                      </Label>
                      <Textarea
                        id="entrance_info"
                        rows={2}
                        data-testid="studio-form-entrance-info"
                        value={formData.entrance_info}
                        onChange={(e) => setFormData({ ...formData, entrance_info: e.target.value })}
                        placeholder="Vom Parkplatz aus folgst du dem Weg zum Eingang (siehe Video) – und schon bist du bei uns."
                      />
                    </div>

                    <div className="space-y-1">
                      <Label htmlFor="parking_page_url" className="flex items-center gap-2">
                        <ArrowRight className="h-4 w-4 text-sky-600" /> Parkplatz-/Anfahrts-Seite (Link)
                      </Label>
                      <Input
                        id="parking_page_url"
                        type="url"
                        data-testid="studio-form-parking-url"
                        value={formData.parking_page_url}
                        onChange={(e) => setFormData({ ...formData, parking_page_url: e.target.value })}
                        placeholder="https://www.strongfit.ch/anfahrt"
                      />
                    </div>

                    <div className="space-y-1">
                      <Label htmlFor="location_video_url" className="flex items-center gap-2">
                        <Video className="h-4 w-4 text-sky-600" /> Video (Weg zum Eingang)
                      </Label>
                      {formData.location_video_url && (
                        <video
                          src={formData.location_video_url.startsWith('http') ? formData.location_video_url : `${BACKEND_URL}${formData.location_video_url}`}
                          controls
                          className="w-full max-w-xs rounded-md border mb-2"
                          data-testid="studio-location-video-preview"
                        />
                      )}
                      <Input
                        type="file"
                        accept="video/mp4,video/quicktime"
                        data-testid="studio-form-location-video-upload"
                        onChange={handleLocationVideoUpload}
                        disabled={uploadingVideo}
                      />
                      {uploadingVideo && <p className="text-xs text-gray-500">Lädt hoch… (max. 16 MB)</p>}
                      <Input
                        id="location_video_url"
                        type="url"
                        className="mt-1"
                        data-testid="studio-form-video-url"
                        value={formData.location_video_url}
                        onChange={(e) => setFormData({ ...formData, location_video_url: e.target.value })}
                        placeholder="… oder externen Video-Link einfügen"
                      />
                    </div>

                    <div className="space-y-1">
                      <Label className="flex items-center gap-2">
                        <Upload className="h-4 w-4 text-sky-600" /> Standortfoto
                      </Label>
                      {formData.location_image_url && (
                        <img
                          src={formData.location_image_url.startsWith('http') ? formData.location_image_url : `${BACKEND_URL}${formData.location_image_url}`}
                          alt="Standort"
                          className="w-full max-w-xs rounded-md border mb-2"
                          data-testid="studio-location-image-preview"
                        />
                      )}
                      <Input
                        type="file"
                        accept="image/*"
                        data-testid="studio-form-location-image-upload"
                        onChange={handleLocationImageUpload}
                        disabled={uploadingImage}
                      />
                      {!editingStudio?.id && (
                        <p className="text-xs text-amber-600">Studio zuerst speichern, danach Foto/Video hochladen.</p>
                      )}
                      {uploadingImage && <p className="text-xs text-gray-500">Lädt hoch…</p>}
                    </div>

                    <div className="rounded-md border border-sky-100 bg-white p-2 space-y-2">
                      <p className="text-xs font-medium text-gray-700">WhatsApp-Vorlagen verbinden</p>
                      <div className="space-y-1">
                        <Label className="text-xs text-gray-600">Foto-Vorlage (Bild-Kopf)</Label>
                        <WhatsAppTemplateSelect
                          templates={waTemplates}
                          value={formData.location_wa_foto_template}
                          onValueChange={(v) => setFormData({ ...formData, location_wa_foto_template: v })}
                          placeholder="Vorlage suchen… (z.B. probetraining_standort_foto2)"
                          testId="studio-wa-foto-template"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs text-gray-600">Video-Vorlage (Video-Kopf)</Label>
                        <WhatsAppTemplateSelect
                          templates={waTemplates}
                          value={formData.location_wa_video_template}
                          onValueChange={(v) => setFormData({ ...formData, location_wa_video_template: v })}
                          placeholder="Vorlage suchen… (z.B. probetraining_standort_video2)"
                          testId="studio-wa-video-template"
                        />
                      </div>
                      <p className="text-[11px] text-gray-500">Leer lassen = Standard-Vorlagen (probetraining_standort_foto2 / _video2).</p>
                    </div>

                    <div className="rounded-md border border-sky-100 bg-white p-2 space-y-2">
                      <p className="text-xs text-gray-600 leading-relaxed">
                        WhatsApp sendet zwei Nachrichten (Foto + Video) über je eine bei Meta
                        freigegebene Vorlage. Zuerst Foto &amp; Video hochladen und speichern, dann einreichen.
                        {waTplStatus && (
                          <span className="block font-medium mt-1" data-testid="studio-wa-template-status-label">
                            Foto: {waTplStatus.foto || '–'} · Video: {waTplStatus.video || '–'}
                          </span>
                        )}
                      </p>
                      <div className="flex flex-wrap gap-2">
                        <Button type="button" size="sm" variant="outline" disabled={waTplBusy}
                          onClick={handleSubmitLocationTemplate} data-testid="studio-wa-template-submit">
                          <Send className="h-3.5 w-3.5 mr-1" /> WhatsApp-Vorlagen einreichen
                        </Button>
                        <Button type="button" size="sm" variant="ghost" disabled={waTplBusy}
                          onClick={handleCheckLocationTemplate} data-testid="studio-wa-template-status">
                          Status prüfen
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* ============ Payrexx Studio-Konfiguration (Franchise) ============ */}
              <div className="rounded-md border border-teal-100 bg-teal-50/40 p-3 space-y-3">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="flex items-center gap-2 text-sm font-medium text-gray-900">
                      <CreditCard className="h-4 w-4 text-teal-600" />
                      Studio-eigenes Payrexx-Konto
                    </div>
                    <p className="text-xs text-gray-600 mt-1 leading-relaxed">
                      Wenn aktiviert, werden alle Zahlungen dieses Studios über
                      sein eigenes Payrexx-Konto abgewickelt. Andernfalls läuft
                      alles über das Hauptkonto.
                    </p>
                  </div>
                  <Switch
                    checked={formData.payrexx_enabled}
                    onCheckedChange={(checked) => setFormData({ ...formData, payrexx_enabled: !!checked })}
                    data-testid="studio-form-payrexx-enabled"
                  />
                </div>

                {formData.payrexx_enabled && (
                  <div className="space-y-3 pt-2 border-t border-teal-100">
                    <div className="space-y-2">
                      <Label htmlFor="payrexx_instance_name" className="text-xs">
                        Payrexx Instance-Name *
                      </Label>
                      <Input
                        id="payrexx_instance_name"
                        data-testid="studio-form-payrexx-instance"
                        value={formData.payrexx_instance_name}
                        onChange={(e) => setFormData({ ...formData, payrexx_instance_name: e.target.value })}
                        placeholder="z.B. strongfit-wetzikon"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="payrexx_api_key" className="text-xs flex items-center gap-2">
                        Payrexx API-Key *
                        {editingStudio?.payrexx_api_key_set && (
                          <span className="text-[10px] bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded">
                            gespeichert
                          </span>
                        )}
                      </Label>
                      <Input
                        id="payrexx_api_key"
                        data-testid="studio-form-payrexx-apikey"
                        type="password"
                        autoComplete="new-password"
                        value={formData.payrexx_api_key}
                        onChange={(e) => setFormData({ ...formData, payrexx_api_key: e.target.value })}
                        placeholder={editingStudio?.payrexx_api_key_set ? '•••••••••• (leer = nicht ändern)' : 'API-Key eintragen'}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="payrexx_webhook_secret" className="text-xs flex items-center gap-2">
                        Webhook-Secret (optional)
                        {editingStudio?.payrexx_webhook_secret_set && (
                          <span className="text-[10px] bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded">
                            gespeichert
                          </span>
                        )}
                      </Label>
                      <Input
                        id="payrexx_webhook_secret"
                        data-testid="studio-form-payrexx-webhook"
                        type="password"
                        autoComplete="new-password"
                        value={formData.payrexx_webhook_secret}
                        onChange={(e) => setFormData({ ...formData, payrexx_webhook_secret: e.target.value })}
                        placeholder={editingStudio?.payrexx_webhook_secret_set ? '•••••••••• (leer = nicht ändern)' : 'Webhook-Secret eintragen'}
                      />
                    </div>
                    <p className="text-[11px] text-gray-500 leading-relaxed">
                      Hinweis: Aus Sicherheitsgründen werden hinterlegte
                      Schlüssel niemals zurück angezeigt. Lasse die Felder
                      leer, um den bestehenden Wert zu behalten.
                    </p>
                  </div>
                )}
              </div>

              {/* ============ Rechnungs-Layout-Override (Franchise) ============ */}
              <div className="rounded-md border border-amber-100 bg-amber-50/40 p-3 space-y-3">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="flex items-center gap-2 text-sm font-medium text-gray-900">
                      <span className="text-amber-600">📄</span>
                      Eigenes Rechnungs-Layout
                    </div>
                    <p className="text-xs text-gray-600 mt-1 leading-relaxed">
                      Optional: Adresse, UID, IBAN etc. für Rechnungen dieses
                      Studios überschreiben. Leere Felder = Standard-Werte aus
                      „Rechnungs-Einstellungen" werden verwendet.
                    </p>
                  </div>
                  <Switch
                    checked={formData.invoice_override_enabled}
                    onCheckedChange={(checked) => setFormData({ ...formData, invoice_override_enabled: !!checked })}
                    data-testid="studio-form-invoice-override-enabled"
                  />
                </div>

                {formData.invoice_override_enabled && (
                  <div className="space-y-4 pt-2 border-t border-amber-100">
                    {/* Adress-Block */}
                    <div className="space-y-2">
                      <div className="text-[11px] uppercase tracking-wide text-amber-700 font-semibold">
                        Briefkopf / Absender
                      </div>
                      <div className="grid grid-cols-1 gap-2">
                        <Input
                          placeholder="Firmenname (leer = StrongFit GmbH)"
                          value={formData.company_name}
                          onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                          data-testid="studio-form-company-name"
                        />
                        <Input
                          placeholder="Strasse"
                          value={formData.company_street}
                          onChange={(e) => setFormData({ ...formData, company_street: e.target.value })}
                          data-testid="studio-form-company-street"
                        />
                      </div>
                      <div className="grid grid-cols-3 gap-2">
                        <Input
                          placeholder="PLZ"
                          value={formData.company_postal_code}
                          onChange={(e) => setFormData({ ...formData, company_postal_code: e.target.value })}
                          data-testid="studio-form-company-plz"
                        />
                        <Input
                          className="col-span-2"
                          placeholder="Ort"
                          value={formData.company_city}
                          onChange={(e) => setFormData({ ...formData, company_city: e.target.value })}
                          data-testid="studio-form-company-city"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <Input
                          placeholder="Telefon (sonst Studio-Tel)"
                          value={formData.company_phone}
                          onChange={(e) => setFormData({ ...formData, company_phone: e.target.value })}
                          data-testid="studio-form-company-phone"
                        />
                        <Input
                          type="email"
                          placeholder="E-Mail (sonst Studio-Mail)"
                          value={formData.company_email}
                          onChange={(e) => setFormData({ ...formData, company_email: e.target.value })}
                          data-testid="studio-form-company-email"
                        />
                      </div>
                      <Input
                        placeholder="UID-Nr. (CHE-...)"
                        value={formData.tax_number}
                        onChange={(e) => setFormData({ ...formData, tax_number: e.target.value })}
                        data-testid="studio-form-uid"
                      />
                    </div>

                    {/* Bank-Block */}
                    <div className="space-y-2">
                      <div className="text-[11px] uppercase tracking-wide text-amber-700 font-semibold">
                        QR-Rechnung / Bankverbindung
                      </div>
                      <Input
                        placeholder="IBAN (z.B. CH00 ...)"
                        value={formData.bank_iban}
                        onChange={(e) => setFormData({ ...formData, bank_iban: e.target.value })}
                        data-testid="studio-form-iban"
                      />
                      <div className="grid grid-cols-2 gap-2">
                        <Input
                          placeholder="Kontoinhaber"
                          value={formData.bank_account_holder}
                          onChange={(e) => setFormData({ ...formData, bank_account_holder: e.target.value })}
                          data-testid="studio-form-iban-holder"
                        />
                        <Input
                          placeholder="Bank-Name"
                          value={formData.bank_name}
                          onChange={(e) => setFormData({ ...formData, bank_name: e.target.value })}
                          data-testid="studio-form-bank-name"
                        />
                      </div>
                    </div>

                    {/* Logo + Präfix */}
                    <div className="space-y-2">
                      <div className="text-[11px] uppercase tracking-wide text-amber-700 font-semibold">
                        Logo &amp; Rechnungsnummer
                      </div>
                      <Input
                        placeholder="Logo-URL (leer = StrongFit-Logo)"
                        value={formData.logo_url}
                        onChange={(e) => setFormData({ ...formData, logo_url: e.target.value })}
                        data-testid="studio-form-logo-url"
                      />
                      <Input
                        placeholder='Rechnungs-Präfix (z.B. "RW" → RW-2026-001)'
                        value={formData.invoice_prefix}
                        onChange={(e) => setFormData({ ...formData, invoice_prefix: e.target.value })}
                        data-testid="studio-form-invoice-prefix"
                      />
                      <Input
                        type="number"
                        min="0"
                        placeholder="Zahlungsziel in Tagen (leer = Standard 30)"
                        value={formData.payment_terms_days}
                        onChange={(e) => setFormData({ ...formData, payment_terms_days: e.target.value })}
                        data-testid="studio-form-payment-terms-days"
                      />
                    </div>

                    <p className="text-[11px] text-gray-500 leading-relaxed">
                      💡 Du musst nicht alle Felder ausfüllen — leere Felder
                      übernehmen automatisch den Standard-Wert aus
                      „Rechnungs-Einstellungen".
                    </p>
                  </div>
                )}
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} data-testid="studio-form-cancel">
                Abbrechen
              </Button>
              <Button type="submit" className="bg-teal-600 hover:bg-teal-700" data-testid="studio-form-submit">
                {editingStudio ? 'Speichern' : 'Erstellen'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StudiosPage;
